import React, { createContext, useContext, useLayoutEffect, useState } from "react";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { isProbablyIpad } from "../utils/orientationLock";
import { getPhoneModes } from "./usePhoneNavigation";

// A window this size has the tablet's room even when the device can't be identified
// as one (no touch points); the height check leaves landscape phones out.
export const TABLET_SIZED_WINDOW = "(min-width:600px) and (max-width:959.95px) and (min-height:600px)";

export const TabletPortraitContext = createContext(false);
export const TabletContext = createContext(false);
export const PageHeaderModeContext = createContext("desktop");

export const isTabletLayoutDevice = (target) => {
  if (!target) return false;
  const ua = String(target.navigator?.userAgent || "");
  const tablet = isProbablyIpad(target)
    || (/Android/i.test(ua) && !/Mobile/i.test(ua))
    || /Tablet|PlayBook|Silk/i.test(ua);
  return tablet && target.innerWidth >= 600;
};

export const isPortraitTablet = (target) => {
  if (!isTabletLayoutDevice(target)) return false;
  // The software keyboard can shorten a portrait viewport below its width.
  const orientation = target.screen?.orientation?.type;
  const portrait = orientation
    ? orientation.startsWith("portrait")
    : typeof target.orientation === "number"
      ? Math.abs(target.orientation) !== 90
      : target.innerHeight > target.innerWidth;
  return portrait;
};

export const TabletPortraitProvider = ({ children }) => {
  const readModes = () => {
    const target = typeof window === "undefined" ? null : window;
    // A window with a tablet's size gets the same layouts, even if the device can't be identified as one.
    const sized = Boolean(target && target.matchMedia && target.matchMedia(TABLET_SIZED_WINDOW).matches);
    const tablet = isTabletLayoutDevice(target);
    const phone = Boolean(target) && (getPhoneModes(target).isPhoneNavigation || target.innerWidth < 600);
    return {
      tablet,
      portrait: isPortraitTablet(target) || sized,
      header: phone ? "phone" : tablet || sized ? "tablet" : "desktop",
    };
  };
  const [modes, setModes] = useState(readModes);
  useLayoutEffect(() => {
    const sync = () => {
      const next = readModes();
      document.documentElement.toggleAttribute("data-tablet-portrait", next.portrait);
      // The page header pattern applies everywhere; data-page-header says which variation.
      document.documentElement.toggleAttribute("data-tablet-header", true);
      document.documentElement.setAttribute("data-page-header", next.header);
      setModes(previous => previous.tablet === next.tablet && previous.portrait === next.portrait && previous.header === next.header ? previous : next);
    };
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      document.documentElement.removeAttribute("data-tablet-portrait");
      document.documentElement.removeAttribute("data-tablet-header");
      document.documentElement.removeAttribute("data-page-header");
    };
  }, []);
  return <TabletContext.Provider value={modes.tablet}>
    <TabletPortraitContext.Provider value={modes.portrait}>
      <PageHeaderModeContext.Provider value={modes.header}>{children}</PageHeaderModeContext.Provider>
    </TabletPortraitContext.Provider>
  </TabletContext.Provider>;
};

export const useTablet = () => useContext(TabletContext);

/** Whether the site menu floats over the page, as it does on a phone, instead of taking layout width. */
export const useTabletNavigation = () => {
  const tablet = useContext(TabletContext);
  const sized = useMediaQuery(TABLET_SIZED_WINDOW, { noSsr: true });
  return tablet || sized;
};

/** Which variation of the page header applies: "phone", "tablet" or "desktop". CSS reads html[data-page-header]. */
export const usePageHeaderMode = () => useContext(PageHeaderModeContext);

export default function useTabletPortrait() {
  return useContext(TabletPortraitContext);
}
